import { yupResolver } from "@hookform/resolvers/yup";
import { Key, Person, Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from "@mui/lab";
import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { LoginRequest, useLoginMutation } from "../service/AuthService";
import { clearAuth, saveAuth } from "../store";

const validation = Yup.object<LoginRequest>().shape({
  username: Yup.string().min(3, "Usuário inválido").required("Informe o nome de usuário"),
  password: Yup.string().min(5, "Senha inválida").required("Informe a senha"),
});

// ─── Logo mark ───────────────────────────────────────────────────────────────
const LogoMark = ({ size = 40, light = false }: { size?: number; light?: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box sx={{
      width: size, height: size, borderRadius: '10px',
      background: light
        ? 'rgba(255,255,255,0.15)'
        : 'linear-gradient(135deg, #14b8a0 0%, #0d9488 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: light ? 'blur(8px)' : 'none',
      border: light ? '1px solid rgba(255,255,255,0.2)' : 'none',
      boxShadow: light ? 'none' : '0 4px 16px rgba(20,184,160,0.35)',
    }}>
      <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
        <rect x="2" y="3" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
        <path d="M8 21h8M12 17v4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="10" r="3" fill="white" opacity="0.9"/>
      </svg>
    </Box>
    <Typography sx={{
      fontSize: size * 0.45,
      fontWeight: 700,
      color: light ? '#ffffff' : 'text.primary',
      letterSpacing: '-0.3px',
      lineHeight: 1,
    }}>
      MídiaTV
    </Typography>
  </Box>
)

// ─── Ilustração de displays flutuando ────────────────────────────────────────
const DisplaysIllustration = () => (
  <svg viewBox="0 0 520 340" fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, opacity: 0.9 }}>

    {/* Grade de pontos */}
    <defs>
      <pattern id="dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.06)" />
      </pattern>
      <filter id="glow">
        <feGaussianBlur stdDeviation="3" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    <rect width="520" height="340" fill="url(#dots)" />

    {/* Glow teal central */}
    <ellipse cx="260" cy="170" rx="220" ry="160"
      fill="radial-gradient(circle, rgba(20,184,160,0.15) 0%, transparent 70%)" opacity="0.4"/>
    <circle cx="260" cy="170" r="180" fill="rgba(20,184,160,0.04)" />

    {/* ── TV grande — centro ── */}
    <g transform="translate(130, 60)">
      {/* Corpo */}
      <rect x="0" y="0" width="260" height="160" rx="10"
        fill="#0d2530" stroke="rgba(20,184,160,0.4)" strokeWidth="1.5"/>
      {/* Tela interna */}
      <rect x="10" y="10" width="240" height="140" rx="6" fill="#081a22"/>
      {/* Topbar */}
      <rect x="10" y="10" width="240" height="28" rx="6" fill="#0d2d3a"/>
      <circle cx="28" cy="24" r="5" fill="rgba(20,184,160,0.7)" filter="url(#glow)"/>
      <rect x="40" y="19" width="60" height="10" rx="3" fill="rgba(255,255,255,0.12)"/>
      <rect x="190" y="18" width="50" height="12" rx="3" fill="rgba(20,184,160,0.25)"
        stroke="rgba(20,184,160,0.5)" strokeWidth="0.8"/>
      {/* Cards de conteúdo */}
      <rect x="18" y="48" width="70" height="44" rx="5" fill="#0f2e3a" stroke="rgba(20,184,160,0.2)" strokeWidth="0.8"/>
      <rect x="22" y="54" width="28" height="16" rx="3" fill="rgba(20,184,160,0.2)"/>
      <rect x="22" y="74" width="50" height="6" rx="2" fill="rgba(255,255,255,0.1)"/>
      <rect x="22" y="83" width="36" height="4" rx="2" fill="rgba(255,255,255,0.06)"/>

      <rect x="96" y="48" width="70" height="44" rx="5" fill="#0f2e3a" stroke="rgba(20,184,160,0.2)" strokeWidth="0.8"/>
      <rect x="100" y="54" width="28" height="16" rx="3" fill="rgba(20,184,160,0.15)"/>
      <rect x="100" y="74" width="50" height="6" rx="2" fill="rgba(255,255,255,0.1)"/>
      <rect x="100" y="83" width="36" height="4" rx="2" fill="rgba(255,255,255,0.06)"/>

      <rect x="174" y="48" width="70" height="44" rx="5" fill="#0f2e3a" stroke="rgba(20,184,160,0.2)" strokeWidth="0.8"/>
      <rect x="178" y="54" width="28" height="16" rx="3" fill="rgba(52,211,153,0.15)"/>
      <rect x="178" y="74" width="50" height="6" rx="2" fill="rgba(255,255,255,0.1)"/>
      <rect x="178" y="83" width="36" height="4" rx="2" fill="rgba(255,255,255,0.06)"/>

      {/* Barra de progresso */}
      <rect x="18" y="104" width="224" height="4" rx="2" fill="rgba(255,255,255,0.05)"/>
      <rect x="18" y="104" width="140" height="4" rx="2" fill="rgba(20,184,160,0.5)"/>

      {/* Linha de lista */}
      <rect x="18" y="116" width="180" height="6" rx="2" fill="rgba(255,255,255,0.06)"/>
      <rect x="18" y="126" width="130" height="6" rx="2" fill="rgba(255,255,255,0.04)"/>
      <rect x="18" y="136" width="155" height="6" rx="2" fill="rgba(255,255,255,0.06)"/>

      {/* Badge status */}
      <rect x="196" y="116" width="46" height="16" rx="8"
        fill="rgba(20,184,160,0.2)" stroke="rgba(20,184,160,0.5)" strokeWidth="0.8"/>
      <circle cx="207" cy="124" r="3" fill="#14b8a0" filter="url(#glow)"/>
      <rect x="213" y="120" width="22" height="8" rx="2" fill="rgba(255,255,255,0.2)"/>

      {/* Suporte */}
      <rect x="120" y="160" width="20" height="12" rx="2" fill="#0d2530"/>
      <rect x="105" y="172" width="50" height="4" rx="2" fill="#0d2530"/>
    </g>

    {/* ── TV pequena — esquerda baixo ── */}
    <g transform="translate(20, 200)" opacity="0.75">
      <rect x="0" y="0" width="140" height="88" rx="7"
        fill="#0b1e28" stroke="rgba(20,184,160,0.25)" strokeWidth="1"/>
      <rect x="7" y="7" width="126" height="74" rx="4" fill="#060f14"/>
      <rect x="7" y="7" width="126" height="18" rx="4" fill="#0c2230"/>
      <circle cx="18" cy="16" r="4" fill="rgba(20,184,160,0.5)"/>
      <rect x="26" y="12" width="40" height="8" rx="2" fill="rgba(255,255,255,0.08)"/>
      <rect x="14" y="34" width="50" height="30" rx="4" fill="#0e2535" stroke="rgba(20,184,160,0.15)" strokeWidth="0.8"/>
      <rect x="18" y="38" width="20" height="12" rx="2" fill="rgba(20,184,160,0.2)"/>
      <rect x="18" y="54" width="38" height="4" rx="2" fill="rgba(255,255,255,0.07)"/>
      <rect x="72" y="34" width="55" height="6" rx="2" fill="rgba(255,255,255,0.07)"/>
      <rect x="72" y="44" width="40" height="6" rx="2" fill="rgba(255,255,255,0.05)"/>
      <rect x="72" y="54" width="48" height="6" rx="2" fill="rgba(255,255,255,0.07)"/>
      <rect x="62" y="88" width="16" height="8" rx="1" fill="#0b1e28"/>
      <rect x="54" y="96" width="32" height="3" rx="1.5" fill="#0b1e28"/>
    </g>

    {/* ── TV pequena — direita baixo ── */}
    <g transform="translate(358, 210)" opacity="0.65">
      <rect x="0" y="0" width="130" height="82" rx="7"
        fill="#0b1e28" stroke="rgba(52,211,153,0.2)" strokeWidth="1"/>
      <rect x="7" y="7" width="116" height="68" rx="4" fill="#060f14"/>
      <rect x="7" y="7" width="116" height="18" rx="4" fill="#0c2230"/>
      <circle cx="18" cy="16" r="4" fill="rgba(52,211,153,0.5)"/>
      <rect x="26" y="12" width="35" height="8" rx="2" fill="rgba(255,255,255,0.08)"/>
      {/* Gráfico de barras mini */}
      {[0,1,2,3,4].map((i) => (
        <rect key={i} x={14 + i * 20} y={58 - [18,26,14,30,22][i]} width="14" height={[18,26,14,30,22][i]}
          rx="2" fill={i === 3 ? 'rgba(20,184,160,0.6)' : 'rgba(20,184,160,0.2)'} />
      ))}
      <rect x="14" y="60" width="100" height="1" fill="rgba(255,255,255,0.08)"/>
      <rect x="58" y="82" width="14" height="7" rx="1" fill="#0b1e28"/>
      <rect x="50" y="89" width="30" height="3" rx="1.5" fill="#0b1e28"/>
    </g>

    {/* ── TV flutuante — topo direita ── */}
    <g transform="translate(370, 28)" opacity="0.55">
      <rect x="0" y="0" width="110" height="68" rx="6"
        fill="#0b1e28" stroke="rgba(20,184,160,0.18)" strokeWidth="1"/>
      <rect x="6" y="6" width="98" height="56" rx="3" fill="#060f14"/>
      <rect x="6" y="6" width="98" height="16" rx="3" fill="#0c2230"/>
      <circle cx="15" cy="14" r="3" fill="rgba(20,184,160,0.4)"/>
      {/* Linhas de playlist */}
      <rect x="12" y="28" width="85" height="5" rx="2" fill="rgba(255,255,255,0.06)"/>
      <rect x="12" y="37" width="60" height="5" rx="2" fill="rgba(20,184,160,0.15)"/>
      <rect x="12" y="46" width="75" height="5" rx="2" fill="rgba(255,255,255,0.06)"/>
      <rect x="12" y="55" width="50" height="5" rx="2" fill="rgba(255,255,255,0.04)"/>
    </g>

    {/* Linhas de conexão entre TVs */}
    <line x1="160" y1="230" x2="200" y2="220" stroke="rgba(20,184,160,0.15)" strokeWidth="1" strokeDasharray="4 4"/>
    <line x1="390" y1="220" x2="350" y2="225" stroke="rgba(20,184,160,0.12)" strokeWidth="1" strokeDasharray="4 4"/>
    <line x1="425" y1="96" x2="395" y2="220" stroke="rgba(20,184,160,0.1)" strokeWidth="1" strokeDasharray="4 4"/>

    {/* Pontos de conexão pulsantes */}
    <circle cx="200" cy="218" r="4" fill="rgba(20,184,160,0.6)" filter="url(#glow)"/>
    <circle cx="350" cy="225" r="3" fill="rgba(52,211,153,0.5)" filter="url(#glow)"/>
    <circle cx="425" cy="96" r="3" fill="rgba(20,184,160,0.4)" filter="url(#glow)"/>

    {/* Overlay gradiente para text readability */}
    <rect width="520" height="340"
      fill="url(#overlay)" opacity="1"/>
    <defs>
      <linearGradient id="overlay" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#0a1920" stopOpacity="0.75"/>
        <stop offset="30%"  stopColor="#0a1920" stopOpacity="0.15"/>
        <stop offset="60%"  stopColor="#0a1920" stopOpacity="0.15"/>
        <stop offset="100%" stopColor="#0a1920" stopOpacity="0.92"/>
      </linearGradient>
    </defs>
  </svg>
)

// ─── Painel esquerdo (branding) ───────────────────────────────────────────────
const BrandPanel = () => (
  <Box sx={{
    flex: 1,
    background: 'linear-gradient(160deg, #0d1e26 0%, #0a1920 50%, #061419 100%)',
    display: 'flex', flexDirection: 'column',
    justifyContent: 'space-between',
    p: '48px',
    position: 'relative', overflow: 'hidden',
  }}>
    {/* Ilustração de fundo */}
    <DisplaysIllustration />

    {/* Glow teal suave sobre a ilustração */}
    <Box sx={{
      position: 'absolute', top: '10%', left: '20%',
      width: '400px', height: '400px', borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(20,184,160,0.08) 0%, transparent 65%)',
      pointerEvents: 'none',
    }} />

    {/* Logo */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <LogoMark size={44} light />
    </Box>

    {/* Conteúdo central */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography sx={{
        fontSize: 36, fontWeight: 700, color: '#ffffff',
        lineHeight: 1.2, letterSpacing: '-0.5px', mb: 2,
      }}>
        Gerencie suas<br />
        <Box component="span" sx={{
          background: 'linear-gradient(90deg, #14b8a0, #34d399)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          telas digitais
        </Box>
        <br />com facilidade.
      </Typography>
      <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, maxWidth: 340 }}>
        Playlists, mídias e displays em um só lugar. Configure, publique e acompanhe em tempo real.
      </Typography>
    </Box>

    {/* Features */}
    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[
        { label: 'Playlists dinâmicas', desc: 'Imagens, vídeos e tabelas de preço' },
        { label: 'Atualização em tempo real', desc: 'WebSocket direto para as TVs' },
        { label: 'Multi-display', desc: 'Gerencie todas as telas de um painel' },
      ].map((f) => (
        <Box key={f.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #14b8a0, #34d399)',
            boxShadow: '0 0 8px rgba(20,184,160,0.6)',
          }} />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
              {f.label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
              {f.desc}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
)

// ─── LoginPage ────────────────────────────────────────────────────────────────
const LoginPage = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('midia_remember') === '1'
  )

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginRequest>({
    resolver: yupResolver(validation),
  })

  useEffect(() => {
    const saved = localStorage.getItem('midia_username')
    if (saved) setValue('username', saved)
  }, [setValue])

  const [login, { isLoading }] = useLoginMutation()

  async function handleLogin(form: LoginRequest) {
    const res = await login(form)
    const data = res.data
    if (!data?.token) return
    if (rememberMe) {
      saveAuth(data, data.token)
      localStorage.setItem('midia_username', form.username)
      localStorage.setItem('midia_remember', '1')
    } else {
      clearAuth()
      localStorage.removeItem('midia_username')
      localStorage.removeItem('midia_remember')
    }
    // Volta para a rota que originou o login (ex.: /media?shared=1 vindo do
    // compartilhamento do PWA). O ProtectedRoute salva a origem em state.from.
    // Fallback para /dashboard quando o login foi acesso direto.
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from
    const dest = from?.pathname ? `${from.pathname}${from.search ?? ''}` : '/dashboard'
    navigate(dest, { replace: true })
  }

  const teal    = '#14b8a0'
  const borderC = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'

  // ─── input sx: inputs 12% menores, contraste melhorado ──────────────────────
  const inputSx = {
    '& .MuiOutlinedInput-input': { py: '11.5px', fontSize: 15 },
    '& .MuiInputLabel-root': { color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)' },
    '& .MuiInputLabel-root.Mui-focused': { color: teal },
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: teal },
    },
  }

  const iconColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'

  const btnSx = {
    height: isMobile ? 50 : 48,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: '0.2px',
    borderRadius: '10px',
    background: `linear-gradient(135deg, ${teal} 0%, #0d9488 100%)`,
    boxShadow: '0 4px 14px rgba(20,184,160,0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0d9488 0%, #0a7a70 100%)',
      boxShadow: '0 6px 20px rgba(20,184,160,0.42)',
      transform: 'translateY(-1px)',
    },
    '&:active': { transform: 'translateY(0)' },
    '&.MuiLoadingButton-loading': {
      background: isDark ? 'rgba(20,184,160,0.18)' : 'rgba(20,184,160,0.12)',
    },
  }

  // ─── Formulário compartilhado desktop + mobile ───────────────────────────────
  const formContent = (
    <Box component="form" onSubmit={handleSubmit(handleLogin)} noValidate
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <TextField
        label="Usuário"
        fullWidth
        autoComplete="username"
        autoFocus={!isMobile}
        {...register('username')}
        error={!!errors.username}
        helperText={errors.username?.message}
        inputProps={{ style: { fontSize: 16 } }}
        sx={inputSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person sx={{ fontSize: 17, color: iconColor }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Senha"
        fullWidth
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        {...register('password')}
        error={!!errors.password}
        helperText={errors.password?.message}
        inputProps={{ style: { fontSize: 16 } }}
        sx={inputSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Key sx={{ fontSize: 17, color: iconColor }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setShowPassword(v => !v)}
                edge="end"
                tabIndex={-1}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                sx={{ color: iconColor }}
              >
                {showPassword
                  ? <VisibilityOff sx={{ fontSize: 17 }} />
                  : <Visibility sx={{ fontSize: 17 }} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Ações secundárias */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: -0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              sx={{
                color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
                '&.Mui-checked': { color: teal },
                p: '4px',
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 13, color: 'text.secondary', userSelect: 'none' }}>
              Lembrar login
            </Typography>
          }
          sx={{ m: 0 }}
        />
        <Tooltip title="Em breve" placement="top">
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              color: isDark ? 'rgba(20,184,160,0.6)' : 'rgba(13,148,136,0.7)',
              cursor: 'default',
              userSelect: 'none',
            }}
          >
            Esqueci minha senha
          </Typography>
        </Tooltip>
      </Box>

      <LoadingButton
        type="submit"
        fullWidth
        loading={isLoading}
        variant="contained"
        size="large"
        sx={{ mt: 0.5, ...btnSx }}
      >
        Entrar
      </LoadingButton>
    </Box>
  )

  // ─── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{
        minHeight: '100svh',
        background: 'linear-gradient(160deg, #0d1e26 0%, #0a1920 50%, #061419 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        pt: 'env(safe-area-inset-top)',
      }}>

        {/* Fundo: ilustração com blur reduzido + overlay mais escuro */}
        <Box sx={{
          position: 'absolute', inset: 0,
          filter: 'blur(1.5px)',
          opacity: 0.45,
          transform: 'scale(1.02)',
        }}>
          <DisplaysIllustration />
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(8,20,26,0.55) 0%, rgba(6,14,20,0.65) 100%)',
        }} />

        {/* Topo: logo + tagline — 35% da tela */}
        <Box sx={{
          position: 'relative', zIndex: 1,
          height: '35vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
        }}>
          <LogoMark size={46} light />
          <Typography sx={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.45)',
            textAlign: 'center',
            px: 6,
            lineHeight: 1.55,
          }}>
            Gerencie suas telas digitais com facilidade
          </Typography>
        </Box>

        {/* Card bottom sheet — ocupa o restante, com scroll quando teclado abre */}
        <Box sx={{
          position: 'relative', zIndex: 1,
          flex: 1,
          bgcolor: isDark ? '#0f1e26' : '#fafafa',
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -16px 48px rgba(0,0,0,0.55)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: 3,
          pt: 2.5,
          pb: 'calc(env(safe-area-inset-bottom) + 24px)',
        }}>
          {/* Handle bar */}
          <Box sx={{
            width: 32, height: 4, borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
            mx: 'auto', mb: 2.5,
          }} />

          <Typography sx={{
            fontSize: 20, fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.3px',
            mb: 0.5,
          }}>
            Bem-vindo de volta
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5, lineHeight: 1.5 }}>
            Entre com suas credenciais para continuar
          </Typography>

          {formContent}
        </Box>
      </Box>
    )
  }

  // ─── Desktop: split-screen ───────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: isDark ? '#0a1520' : '#f4f6f8', overflow: 'hidden' }}>

      <BrandPanel />

      <Box sx={{
        width: '420px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        bgcolor: isDark ? '#0f1e26' : '#ffffff',
        borderLeft: `1px solid ${borderC}`,
      }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Box sx={{ mb: 3.5 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px', mb: 0.75 }}>
              Bem-vindo de volta
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              Entre com suas credenciais para continuar
            </Typography>
          </Box>
          {formContent}
        </Box>
      </Box>
    </Box>
  )
}

export default LoginPage
